import type { Map as MapboxMap, StyleSpecification } from 'mapbox-gl';
import { modelStyle } from './apsHqPlacement';
import { CUSTOM_MODEL_EMISSIVE_STRENGTH, type MapLightPreset } from './customModelLighting';

export type HqMapMode = 'standard' | 'satellite';


/** Owns only the HQ layers; the app continues to own the basemap and camera. */
export function attachApsHqModel(
  map: MapboxMap,
  initialMode: HqMapMode,
  onError: (message: string | null) => void,
  initialLightPreset: MapLightPreset = 'day',
) {
  const modelUrl = new URL(
    `${import.meta.env.BASE_URL}models/1401-f-st-batched.glb?v=c8ba72ee96b8`,
    window.location.href,
  ).href;
  return attachSiteModel(map, initialMode, onError, initialLightPreset, {
    name: 'APS HQ', asset: '1401-asset', model: '1401-model', clip: '1401-replacement',
    street: '1401-street-surface', style: modelStyle(modelUrl),
    errorPattern: /1401-(?:f-st|model|asset|replacement|boundary)/i,
  });
}

/** Shared lifecycle for fixed geographic models across basemap replacements. */
export function attachSiteModel(
  map: MapboxMap,
  initialMode: HqMapMode,
  onError: (message: string | null) => void,
  initialLightPreset: MapLightPreset,
  definition: { name: string; asset: string; model: string; clip: string; street?: string;
    style: StyleSpecification; errorPattern: RegExp },
) {
  const { model: MODEL_LAYER, clip: CLIP_LAYER, street: STREET_LAYER } = definition;
  const { sources, layers } = definition.style;
  let mode = initialMode;
  let failed = false;
  let lightPreset = initialLightPreset;

  function syncStreetSurface() {
    if (!STREET_LAYER || !map.getLayer(STREET_LAYER)) return;
    // Keep root-layer paint independent of a fragment that can be replaced.
    // Copy its current settings rather than holding cross-import expressions.
    try {
      const color = map.getConfigProperty('basemap', 'colorRoads');
      const brightness = map.getConfigProperty('basemap', 'roadsBrightness');
      if (typeof color === 'string' && map.getPaintProperty(STREET_LAYER, 'fill-color') !== color)
        map.setPaintProperty(STREET_LAYER, 'fill-color', color);
      if (typeof brightness === 'number' && map.getPaintProperty(STREET_LAYER, 'fill-emissive-strength') !== brightness)
        map.setPaintProperty(STREET_LAYER, 'fill-emissive-strength', brightness);
    } catch { /* Keep the last road appearance while the fragment loads. */ }
  }

  function syncLighting() {
    syncStreetSurface();
    if (!map.getLayer(MODEL_LAYER)) return;
    const strength = CUSTOM_MODEL_EMISSIVE_STRENGTH[lightPreset];
    if (map.getPaintProperty(MODEL_LAYER, 'model-emissive-strength') !== strength) {
      map.setPaintProperty(MODEL_LAYER, 'model-emissive-strength', strength);
    }
  }

  function modelLoaded() {
    if (!map.getLayer(MODEL_LAYER)) return false;
    // Model sources load directly, without the tiled model-manager registry.
    try {
      return !!map.getSource(definition.asset) && map.isSourceLoaded(definition.asset);
    } catch {
      return false;
    }
  }

  function setVisibility(id: string | undefined, visible: boolean) {
    if (!id || !map.getLayer(id)) return;
    const visibility = visible ? 'visible' : 'none';
    if (map.getLayoutProperty(id, 'visibility') !== visibility) {
      map.setLayoutProperty(id, 'visibility', visibility);
    }
  }

  function syncVisibility() {
    const show = mode === 'standard' && !failed;
    setVisibility(MODEL_LAYER, show);
    // Never erase the original building until the GLB has actually loaded.
    setVisibility(CLIP_LAYER, show && modelLoaded());
    setVisibility(STREET_LAYER, show && modelLoaded());
  }

  function fail() {
    failed = true;
    syncVisibility();
    onError(`${definition.name} 3D model could not load. The original map is shown. Reload to retry.`);
  }

  function restore() {
    if (mode !== 'standard' || failed) {
      syncVisibility();
      return;
    }
    // Imported styles can still be initializing when the root fires style.load.
    try { map.getStyle(); } catch { return; }
    try {
      for (const [id, source] of Object.entries(sources)) {
        if (!map.getSource(id)) map.addSource(id, source);
      }
      for (const layer of layers) {
        if (!map.getLayer(layer.id)) {
          map.addLayer({ ...layer, layout: { ...layer.layout, visibility: layer.id === MODEL_LAYER ? 'visible' : 'none' } });
        }
      }
      syncLighting();
      syncVisibility();
    } catch (error) {
      console.error(`${definition.name} model integration failed`, error);
      fail();
    }
  }

  function handleError(event: { error: Error }) {
    const message = event.error?.message || '';
    if (definition.errorPattern.test(message)) fail();
  }

  function handleIdle() {
    if (mode !== 'standard' || failed) return;
    if (!map.getLayer(MODEL_LAYER)) restore();
    syncVisibility();
    // Non-tiled model sources emit errors for failed requests at any zoom.
  }

  function handleSourceData(event: { sourceId?: string }) {
    if (event.sourceId === definition.asset) syncVisibility();
  }

  onError(null);
  map.on('style.load', restore);
  map.on('style.import.load', syncStreetSurface);
  map.on('error', handleError);
  map.on('idle', handleIdle);
  map.on('sourcedata', handleSourceData);
  if (map.isStyleLoaded()) restore();

  return {
    setLightPreset(next: MapLightPreset) {
      lightPreset = next;
      syncLighting();
    },
    setMode(next: HqMapMode) {
      mode = next;
      syncVisibility();
      if (map.isStyleLoaded()) restore();
    },
    dispose() {
      map.off('style.load', restore);
      map.off('style.import.load', syncStreetSurface);
      map.off('error', handleError);
      map.off('idle', handleIdle);
      map.off('sourcedata', handleSourceData);
    },
  };
}
